import {
  type ChannelAsset,
  COVER_DEFAULTS,
  type CoverImage,
  type CoverSource,
  type CoverStyle,
  type CoverTextMode,
  coverStyleValues,
  EVENT_DRAFT_MAX_IMAGES,
  EVENT_FLOW_PRICES,
  EVENT_PACKAGE_CREDIT_COST,
  type EventChannel,
  type EventDescriptionLanguage,
  type EventDraftResponse,
  type ExtraEventChannel,
  type GeneratedAsset,
  type LogoPlacement,
  type PhotoLayout,
  photoComposeCost,
  photoLayoutSchema,
} from '@pulsify/shared/dto'
import * as Dialog from '@radix-ui/react-dialog'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useReducedMotion } from 'framer-motion'
import { Paperclip, RotateCcw, X } from 'lucide-react'
import {
  type ClipboardEvent,
  type DragEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import { useCreditsBalance } from '~/entities/credits/model/use-credits'
import { formatCredits } from '~/entities/subscription/plans'
import { coverBlob, eventFlowApi } from '~/features/ai-studio/api/event-flow-api'
import { translateApi } from '~/features/ai-studio/api/translate-api'
import {
  draftFailureAction,
  draftToHandoff,
  manualHandoff,
  socialNetworkOf,
} from '~/features/ai-studio/lib/event-handoff'
import { addPictures } from '~/features/events/ai-start/pictures'
import { prepareUpload } from '~/features/listing-menus/lib/prepare-upload'
import type { ImageFile } from '~/features/shared/components/images-section'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { extractApiError, getErrorMessageFromError } from '~/shared/lib/api-errors'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { cn } from '~/shared/lib/utils'
import { Label } from '~/shared/ui/label'
import { Textarea } from '~/shared/ui/textarea'
import type { ChatSnapshot } from './ai-flow-draft'
import { BrandColors, ChannelPicker, channelLabel } from './channel-picker'
import {
  answerQuestion,
  type ChatDetails,
  type DetailQuestion,
  detailsFromDraft,
  draftWithDetails,
  nextQuestion,
} from './chat-details'
import {
  AssistantMessage,
  ChatInput,
  Chip,
  Chips,
  FlowAvatar,
  GlassCard,
  InputAction,
  ThinkingDots,
  UserMessage,
} from './chat-ui'
import {
  CategoryQuestion,
  EntryQuestion,
  entryPrompt,
  PlaceQuestion,
  placePrompt,
  ReviewCard,
  WhenQuestion,
} from './details-steps'
import {
  type AssistantKind,
  activeTurnId,
  coverPrice,
  extraChannelsPrice,
  finalizePrice,
  flowReducer,
  initialFlow,
  translationPrice,
} from './flow-machine'
import { PhotoEditor } from './photo-editor'
import { StyleThumb } from './style-thumbs'

type Handoff = ReturnType<typeof draftToHandoff>

/** What the start page hands the chat: each new id starts a fresh package conversation. */
export interface ChatStart {
  id: number
  prompt: string
  pictures: File[]
}

/** The event a cover is made for, read from the form when the cover chat starts. */
export interface CoverSubject {
  title: string
  description: string
  startTime: string | null
}

type EventAiChatProps = {
  businessId: string
  open: boolean
  onOpenChange: (open: boolean) => void
} & (
  | {
      mode: 'event'
      start: ChatStart | null
      onDone: (handoff: Handoff) => void
      restore?: ChatSnapshot | null
      onSnapshot?: (snapshot: ChatSnapshot) => void
    }
  | { mode: 'cover'; subject: () => CoverSubject; onCover: (file: File) => void }
)

const STYLE_TEXT: Record<CoverStyle, { name: () => string; hint: () => string }> = {
  grafik: { name: () => m.aiFlowStyleGrafik(), hint: () => m.aiFlowStyleGrafikHint() },
  poster: { name: () => m.aiFlowStylePoster(), hint: () => m.aiFlowStylePosterHint() },
  '3d': { name: () => m.aiFlowStyle3d(), hint: () => m.aiFlowStyle3dHint() },
  minimal: { name: () => m.aiFlowStyleMinimal(), hint: () => m.aiFlowStyleMinimalHint() },
  typo: { name: () => m.aiFlowStyleTypo(), hint: () => m.aiFlowStyleTypoHint() },
  retro: { name: () => m.aiFlowStyleRetro(), hint: () => m.aiFlowStyleRetroHint() },
}

const LOGO_CHOICES: { value: LogoPlacement; label: () => string }[] = [
  { value: 'top-right', label: () => m.aiFlowLogoTopRight() },
  { value: 'bottom', label: () => m.aiFlowLogoBottom() },
  { value: 'none', label: () => m.aiFlowLogoNone() },
]

const TEXT_CHOICES: { value: CoverTextMode; label: () => string }[] = [
  { value: 'title-date', label: () => m.aiFlowTextTitleDate() },
  { value: 'title', label: () => m.aiFlowTextTitle() },
  { value: 'none', label: () => m.aiFlowTextNone() },
]

const OTHER_LETTERING =
  'Set the text in a clearly different typeface and lettering style that still suits the photo; keep the photo and the exact wording.'

const REFINE_CHIPS: (() => string)[] = [
  () => m.aiFlowRefineDarker(),
  () => m.aiFlowRefineNeon(),
  () => m.aiFlowRefineFewer(),
]

const QUESTION_LABEL: Record<DetailQuestion, () => string> = {
  when: m.aiFlowChangeWhen,
  place: m.aiFlowChangePlace,
  entry: m.aiFlowChangeEntry,
  category: m.aiFlowChangeCategory,
}

const EXAMPLES = [() => m.aiFlowExample1(), () => m.aiFlowExample2(), () => m.aiFlowExample3()]

const GLASS_BUTTON =
  'inline-flex h-10 items-center justify-center gap-2 rounded-full border border-border bg-muted font-medium text-[13px] text-foreground outline-none backdrop-blur-md transition-colors hover:bg-secondary focus-visible:ring-2 focus-visible:ring-ring'

function dateLabel(startTime: string | null): string {
  if (!startTime) return ''
  const date = new Date(startTime)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(getLocale() === 'en' ? 'en-GB' : 'de-AT', {
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function toImageFiles(files: File[]): ImageFile[] {
  return files.map((file, index) => ({
    file,
    preview: URL.createObjectURL(file),
    id: `ai-picture-${index}-${file.size}`,
  }))
}

function paragraphsHtml(text: string): string {
  const escapeHtml = (value: string) =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return Array.from(doc.body.querySelectorAll('p'))
    .map((p) => p.innerText.trim())
    .filter(Boolean)
    .join('\n\n')
}

async function assetToFile(asset: GeneratedAsset): Promise<File> {
  const res = await fetch(asset.url)
  if (!res.ok) throw new Error(`cover ${res.status}`)
  const blob = await res.blob()
  return new File([blob], `${asset.id}.jpg`, { type: blob.type || 'image/jpeg' })
}

/** The full-screen Pulsify chat that fills in an event, or only makes its cover. */
export function EventAiChat(props: EventAiChatProps) {
  const { businessId, open, onOpenChange } = props
  const coverOnly = props.mode === 'cover'
  const queryClient = useQueryClient()
  const reduceMotion = useReducedMotion()
  const formId = useId()
  const restore = props.mode === 'event' ? (props.restore ?? null) : null
  const onSnapshot = props.mode === 'event' ? props.onSnapshot : undefined
  const [machine, dispatch] = useReducer(flowReducer, restore?.machine ?? initialFlow)
  const [input, setInput] = useState('')
  const [pending, setPending] = useState<string | null>(null)
  const [pictures, setPictures] = useState<File[]>(restore?.pictures ?? [])
  const [thinking, setThinking] = useState(false)
  const [screenshotHint, setScreenshotHint] = useState<string | null>(null)
  const [startFailed, setStartFailed] = useState(false)
  const [draft, setDraft] = useState<EventDraftResponse | null>(restore?.draft ?? null)
  const [subject, setSubject] = useState<CoverSubject | null>(restore?.subject ?? null)
  const [variants, setVariants] = useState<CoverImage[]>(restore?.variants ?? [])
  const [coverSource, setCoverSource] = useState<CoverSource>(restore?.coverSource ?? 'graphic')
  const [manualPhoto, setManualPhoto] = useState(false)
  const [chosen, setChosen] = useState(restore?.chosen ?? 0)
  const [made, setMade] = useState<ChannelAsset[]>(restore?.made ?? [])
  const [photo, setPhoto] = useState<File | null>(restore?.photo ?? null)
  const [photoAspect, setPhotoAspect] = useState<number | null>(restore?.photoAspect ?? null)
  const [layout, setLayout] = useState<PhotoLayout>(
    () => restore?.layout ?? photoLayoutSchema.parse({}),
  )
  const [extend, setExtend] = useState<EventChannel[]>(restore?.extend ?? [])
  const [primaryText, setPrimaryText] = useState(restore?.primaryText ?? '')
  const [secondaryText, setSecondaryText] = useState<string | null>(restore?.secondaryText ?? null)
  const [details, setDetails] = useState<ChatDetails | null>(
    () => restore?.details ?? (restore?.draft ? detailsFromDraft(restore.draft) : null),
  )
  const fileInputRef = useRef<HTMLInputElement>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const chatInputRef = useRef<HTMLInputElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const busyRef = useRef(false)
  const handledStartRef = useRef<number | null>(
    restore && props.mode === 'event' ? (props.start?.id ?? null) : null,
  )

  useEffect(() => {
    onSnapshot?.({
      machine,
      pictures,
      draft,
      subject,
      variants,
      coverSource,
      chosen,
      made,
      photo,
      photoAspect,
      layout,
      extend,
      primaryText,
      secondaryText,
      details,
    })
  }, [
    onSnapshot,
    machine,
    pictures,
    draft,
    subject,
    variants,
    coverSource,
    chosen,
    made,
    photo,
    photoAspect,
    layout,
    extend,
    primaryText,
    secondaryText,
    details,
  ])

  const { data: balance } = useCreditsBalance(businessId)
  const brand = useQuery({
    queryKey: queryKey(QueryKey.AI_BRAND, businessId),
    queryFn: () => eventFlowApi.brand(businessId),
    enabled: !!businessId,
  })
  const [colors, setColors] = useState<string[]>([])
  useEffect(() => {
    if (brand.data) setColors(brand.data.colors)
  }, [brand.data])
  const saveColors = useMutation({
    mutationFn: (next: string[]) => eventFlowApi.saveBrandColors(businessId, next),
  })
  const saveTimer = useRef<number | undefined>(undefined)
  const changeColors = (next: string[]) => {
    setColors(next)
    window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(() => saveColors.mutate(next), 600)
  }
  const logoUrl = brand.data?.logoUrl ?? null

  const previews = useMemo(() => pictures.map((file) => URL.createObjectURL(file)), [pictures])
  useEffect(
    () => () => {
      for (const url of previews) URL.revokeObjectURL(url)
    },
    [previews],
  )
  const photoUrl = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo])
  useEffect(() => () => (photoUrl ? URL.revokeObjectURL(photoUrl) : undefined), [photoUrl])
  const variantUrls = useMemo(
    () => variants.map((variant) => URL.createObjectURL(coverBlob(variant))),
    [variants],
  )
  useEffect(
    () => () => {
      for (const url of variantUrls) URL.revokeObjectURL(url)
    },
    [variantUrls],
  )

  const language: EventDescriptionLanguage = getLocale() === 'en' ? 'en' : 'de'
  const otherLanguage: EventDescriptionLanguage = language === 'en' ? 'de' : 'en'
  const languageLabel = (lang: EventDescriptionLanguage) =>
    lang === 'en' ? m.aiFlowDescriptionEnglish() : m.aiFlowDescriptionGerman()

  const activeId = activeTurnId(machine)
  const activeKind = machine.turns.find((turn) => turn.id === activeId)
  const started = machine.turns.length > 0
  const talking = started || pending !== null || (coverOnly && thinking)
  const flowId = machine.flow?.flowId ?? null
  const about: CoverSubject = draft
    ? { title: draft.title, description: draft.description, startTime: draft.startTime }
    : (subject ?? { title: '', description: '', startTime: null })

  // biome-ignore lint/correctness/useExhaustiveDependencies: follow the newest message
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo?.({ top: el.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth' })
  }, [machine.turns.length, thinking, pending, reduceMotion])

  const refreshBalance = () =>
    queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.CREDITS_BALANCE, businessId) })

  async function run<T>(work: () => Promise<T>, retry?: AssistantKind): Promise<T | null> {
    if (busyRef.current) return null
    busyRef.current = true
    setThinking(true)
    try {
      return await work()
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
      if (retry) {
        dispatch({ type: 'say', kind: 'failed' })
        dispatch({ type: 'say', kind: retry })
      }
      return null
    } finally {
      busyRef.current = false
      setThinking(false)
      refreshBalance()
    }
  }

  const resetConversation = () => {
    dispatch({ type: 'reset' })
    setInput('')
    setPending(null)
    setPictures([])
    setScreenshotHint(null)
    setStartFailed(false)
    setDraft(null)
    setSubject(null)
    setVariants([])
    setChosen(0)
    setMade([])
    setPhoto(null)
    setPhotoAspect(null)
    setLayout(photoLayoutSchema.parse({}))
    setExtend([])
    setPrimaryText('')
    setSecondaryText(null)
    setDetails(null)
  }

  const addStartPictures = (files: File[]) => {
    if (files.length === 0) return
    const result = addPictures(pictures, files)
    setPictures(result.pictures)
    if (result.pictures.length > pictures.length) setScreenshotHint(null)
    if (result.skipped > 0) {
      toast.info(m.eventAiStartPicturesSkipped({ count: EVENT_DRAFT_MAX_IMAGES }))
    }
  }

  const preparePictures = async (files: File[]): Promise<File[] | null> => {
    const prepared = await Promise.all(files.map((file) => prepareUpload(file)))
    const ready: File[] = []
    for (const result of prepared) {
      if (result.ok) ready.push(result.file)
      else toast.error(m.eventAiStartPictureUnreadable({ name: result.name }))
    }
    return ready.length < files.length ? null : ready
  }

  const takePhoto = async (file: File) => {
    const ready = await preparePictures([file])
    if (!ready?.[0]) return
    const bitmap = await createImageBitmap(ready[0]).catch(() => null)
    setPhotoAspect(bitmap ? bitmap.width / bitmap.height : null)
    bitmap?.close()
    setPhoto(ready[0])
  }

  const finishEvent = (handoff: Handoff) => {
    if (props.mode !== 'event') return
    onOpenChange(false)
    props.onDone(handoff)
  }

  const startDraft = async (text: string, files: File[]) => {
    setPending(text || m.aiFlowPicturesOnly())
    setInput('')
    const ready = await preparePictures(files)
    if (!ready) {
      setPending(null)
      setInput(text)
      return
    }
    setScreenshotHint(null)
    await run(async () => {
      try {
        const result = await eventFlowApi.draft(businessId, {
          prompt: text,
          pictures: ready,
          locale: getLocale() === 'en' ? 'en' : 'de',
          package: true,
        })
        setDraft(result)
        setPrimaryText(result.description)
        setDetails(detailsFromDraft(result))
        setLayout((current) => ({
          ...current,
          title: result.title,
          subtitle: dateLabel(result.startTime),
        }))
        if (ready[0]) await takePhoto(ready[0])
        dispatch({
          type: 'drafted',
          text: text || m.aiFlowPicturesOnly(),
          cost: EVENT_PACKAGE_CREDIT_COST,
          flow: result.flow,
        })
      } catch (error) {
        setInput(text)
        const apiError = extractApiError(error)
        const action = draftFailureAction(apiError.errorCode, {
          prompt: text,
          pictureCount: ready.length,
        })
        if (action === 'fix-input') {
          toast.error(apiError.message)
        } else if (action === 'add-screenshot') {
          const hint = m.eventAiStartScreenshotHint({ network: socialNetworkOf(text) ?? '' })
          setScreenshotHint(hint)
          toast.info(hint)
        } else {
          toast.info(
            apiError.errorCode === 'EventPageUnreadable'
              ? m.eventAiStartFallbackUnreadable()
              : m.eventAiStartFallbackFailed(),
          )
          finishEvent(manualHandoff(text, toImageFiles(ready)))
        }
      } finally {
        setPending(null)
      }
    })
  }

  const startCover = async () => {
    if (props.mode !== 'cover') return
    const next = props.subject()
    setSubject(next)
    setStartFailed(false)
    setLayout((current) => ({
      ...current,
      title: next.title,
      subtitle: dateLabel(next.startTime),
    }))
    const flow = await run(() => eventFlowApi.start(businessId))
    if (flow) dispatch({ type: 'cover-started', flow })
    else setStartFailed(true)
  }

  const startRequest = props.mode === 'event' ? props.start : null
  // biome-ignore lint/correctness/useExhaustiveDependencies: each start request runs once
  useEffect(() => {
    if (!startRequest || handledStartRef.current === startRequest.id) return
    handledStartRef.current = startRequest.id
    resetConversation()
    setPictures(startRequest.pictures)
    startDraft(startRequest.prompt, startRequest.pictures)
  }, [startRequest])

  // biome-ignore lint/correctness/useExhaustiveDependencies: the cover chat starts once per opening
  useEffect(() => {
    if (coverOnly && open && !started && !busyRef.current && !startFailed) startCover()
  }, [coverOnly, open])

  const restart = () => {
    if (busyRef.current) return
    resetConversation()
    if (coverOnly) startCover()
    else window.setTimeout(() => chatInputRef.current?.focus(), 0)
  }

  const afterDetails = (current: ChatDetails | null): AssistantKind =>
    (current && nextQuestion(current)) ?? 'review'

  const settle = (question: DetailQuestion, patch: Partial<ChatDetails>, label: string) => {
    if (!details) return
    const next = answerQuestion(details, question, patch)
    setDetails(next)
    answer(label, afterDetails(next))
  }

  const askAgain = (question: DetailQuestion) => {
    answer(QUESTION_LABEL[question](), question)
  }

  const answer = (text: string, next: AssistantKind, patch?: Parameters<typeof dispatch>[0]) => {
    if (patch) dispatch(patch)
    dispatch({ type: 'answer', text, next })
  }

  const makeVariants = async (label: string, overrides?: Partial<typeof machine.cover>) => {
    if (!flowId) return
    const cover = { ...machine.cover, ...overrides }
    dispatch({ type: 'answer', text: label, next: 'variants', patch: overrides })
    const result = await run(
      () =>
        eventFlowApi.variants(businessId, {
          flowId,
          style: cover.style,
          logo: logoUrl ? cover.logo : 'none',
          text: cover.text,
          title: about.title,
          dateLabel: dateLabel(about.startTime),
          about: `${about.title}. ${about.description}`.slice(0, 800),
          note: machine.note,
          colors,
        }),
      'cover-choice',
    )
    if (!result) return
    setVariants(result.variants)
    setCoverSource('graphic')
    setChosen(0)
    dispatch({ type: 'charged', cost: coverPrice(machine.flow), flow: result.flow })
  }

  const photoTitle = (layout.title || about.title).trim()

  const makePhotoVariants = async (label: string) => {
    if (!flowId || !photo || !photoTitle) return
    dispatch({ type: 'answer', text: label, next: 'variants' })
    const date = dateLabel(about.startTime)
    const result = await run(
      () =>
        eventFlowApi.photoVariants(businessId, {
          photo,
          fields: {
            flowId,
            text: date ? 'title-date' : 'title',
            title: photoTitle,
            dateLabel: date,
            note: machine.note,
          },
        }),
      'photo',
    )
    if (!result) return
    setVariants(result.variants)
    setCoverSource('photo')
    setChosen(0)
    dispatch({ type: 'charged', cost: coverPrice(machine.flow), flow: result.flow })
  }

  const refine = async (instruction: string, label = instruction) => {
    const variant = variants[chosen]
    if (!variant) return
    dispatch({ type: 'answer', text: label, next: 'refined' })
    const result = await run(
      () => eventFlowApi.refine(businessId, coverBlob(variant), instruction, coverSource),
      'variants',
    )
    if (!result) return
    setVariants((current) => current.map((v, i) => (i === chosen ? result.image : v)))
    dispatch({ type: 'charged', cost: EVENT_FLOW_PRICES.refine })
  }

  const logoChoice = () => ({
    placement: logoUrl ? machine.cover.logo : ('none' as const),
    large: machine.cover.logoLarge,
  })

  const takeVariant = async (label: string) => {
    const variant = variants[chosen]
    if (!variant || !flowId) return
    dispatch({ type: 'answer', text: label, next: 'cover-done' })
    const price = finalizePrice(machine.flow)
    const extra = machine.cover.channels.filter(
      (channel): channel is ExtraEventChannel => channel !== 'pulsify-cover',
    )
    const result = await run(async () => {
      const finished = await eventFlowApi.finalize(businessId, {
        image: coverBlob(variant),
        flowId,
        title: about.title,
        logo: logoChoice(),
        source: coverSource,
      })
      dispatch({ type: 'charged', cost: price, flow: finished.flow })
      const finishedMaster = coverBlob(finished.master)
      const assets: ChannelAsset[] = [{ channel: 'pulsify-cover', asset: finished.cover }]
      if (extra.length > 0) {
        try {
          const formatsPrice = extraChannelsPrice(machine.cover.channels, finished.flow)
          const formats = await eventFlowApi.formats(businessId, {
            master: finishedMaster,
            channels: extra,
            flowId,
            title: about.title,
            logo: logoChoice(),
            source: coverSource,
          })
          dispatch({
            type: 'charged',
            cost: formatsPrice,
            flow: formats.flow ?? undefined,
          })
          assets.push(...formats.assets)
        } catch (error) {
          toast.error(getErrorMessageFromError(error))
        }
      }
      return assets
    }, 'variants')
    if (result) setMade(result)
  }

  const makePhotoFormats = async (label: string) => {
    if (!photo) return
    dispatch({ type: 'answer', text: label, next: 'photo-done' })
    const request = {
      layout: { ...layout, logo: logoUrl ? layout.logo : ('none' as const) },
      channels: machine.cover.channels,
      extend: extend.filter((channel) => machine.cover.channels.includes(channel)),
    }
    const result = await run(
      () =>
        eventFlowApi.photo(businessId, {
          photo,
          request,
          title: layout.title || about.title,
        }),
      'photo',
    )
    if (!result) return
    setMade(result.assets)
    dispatch({ type: 'charged', cost: photoComposeCost(request) })
  }

  const translate = async () => {
    if (!primaryText.trim()) return
    const price = translationPrice(machine.flow)
    const result = await run(() =>
      translateApi.description(businessId, {
        html: paragraphsHtml(primaryText),
        from: language,
        to: otherLanguage,
        flowId: flowId ?? undefined,
      }),
    )
    if (!result) return
    setSecondaryText(htmlToText(result.html))
    const flow = machine.flow
    dispatch({
      type: 'charged',
      cost: price,
      flow:
        flow && price === 0
          ? { ...flow, free: { ...flow.free, translation: false } }
          : (flow ?? undefined),
    })
  }

  const madeCover = made.find((item) => item.channel === 'pulsify-cover')

  const finish = async (autoSave = false) => {
    if (!draft || !details) return
    const images = await run(async (): Promise<ImageFile[]> => {
      if (!madeCover) return toImageFiles(pictures)
      const file = await assetToFile(madeCover.asset)
      return [{ file, preview: URL.createObjectURL(file), id: `ai-cover-${madeCover.asset.id}` }]
    })
    if (!images) return
    const handoff = draftToHandoff(draftWithDetails(draft, details, primaryText), images)
    finishEvent({
      ...handoff,
      values: { ...handoff.values, ticketTypes: details.ticketTypes },
      entry: details.entry,
      autoSave,
      descriptionLanguage: language,
      descriptionTranslations: secondaryText?.trim()
        ? { [otherLanguage]: paragraphsHtml(secondaryText) }
        : {},
      features: {
        liveRoom: details.liveRoom,
        signup: details.entry === 'signup',
        tickets: details.entry === 'link',
      },
    })
  }

  const takeCover = async () => {
    if (props.mode !== 'cover' || !madeCover) return
    const file = await run(() => assetToFile(madeCover.asset))
    if (!file) return
    props.onCover(file)
    onOpenChange(false)
  }

  const submitInput = () => {
    const text = input.trim()
    if (!text) return
    if (!started) {
      if (!coverOnly) startDraft(text, pictures)
      return
    }
    setInput('')
    if (activeKind?.role === 'assistant' && ['variants', 'refined'].includes(activeKind.kind)) {
      refine(text)
    } else {
      dispatch({ type: 'note', text })
    }
  }

  const acceptsStartPictures = !coverOnly && !started && pending === null

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.stopPropagation()
    const files = Array.from(event.clipboardData.files)
    if (files.length === 0) return
    event.preventDefault()
    if (activeKind?.role === 'assistant' && activeKind.kind === 'photo') takePhoto(files[0])
    else if (acceptsStartPictures) addStartPictures(files)
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const files = Array.from(event.dataTransfer.files)
    if (activeKind?.role === 'assistant' && activeKind.kind === 'photo' && files[0]) {
      takePhoto(files[0])
    } else if (acceptsStartPictures) addStartPictures(files)
  }

  const coverTotal =
    coverPrice(machine.flow) + extraChannelsPrice(machine.cover.channels, machine.flow)
  const freeFormats = machine.flow?.free.formats ?? 0
  const photoTotal = photoComposeCost({ channels: machine.cover.channels, extend })
  const inRefine =
    activeKind?.role === 'assistant' && ['variants', 'refined'].includes(activeKind.kind)

  const renderAssistant = (kind: AssistantKind, active: boolean): ReactNode => {
    switch (kind) {
      case 'cover-choice':
        return (
          <>
            {coverOnly ? m.aiChatCoverChoice() : m.aiFlowCoverChoice()}
            {active && (
              <Chips>
                <Chip
                  tone="go"
                  price={coverPrice(machine.flow)}
                  onClick={() => answer(m.aiFlowCoverAi(), 'style')}
                >
                  {m.aiFlowCoverAi()}
                </Chip>
                <Chip price={0} onClick={() => answer(m.aiFlowCoverPhoto(), 'photo')}>
                  {m.aiFlowCoverPhoto()}
                </Chip>
                <Chip
                  tone="quiet"
                  onClick={() =>
                    makeVariants(m.aiFlowJustDoIt(), {
                      ...COVER_DEFAULTS,
                      channels: ['pulsify-cover'],
                    })
                  }
                >
                  {m.aiFlowJustDoIt()}
                </Chip>
                {!coverOnly && (
                  <Chip tone="quiet" onClick={() => answer(m.aiFlowCoverSkip(), 'description')}>
                    {m.aiFlowCoverSkip()}
                  </Chip>
                )}
              </Chips>
            )}
          </>
        )
      case 'style':
        return (
          <>
            {m.aiFlowAskStyle()}
            {active && (
              <>
                <GlassCard className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {coverStyleValues.map((style) => (
                    <button
                      key={style}
                      type="button"
                      onClick={() =>
                        answer(STYLE_TEXT[style].name(), logoUrl ? 'logo' : 'text', {
                          type: 'choose',
                          patch: { style },
                        })
                      }
                      className="flex flex-col gap-1.5 rounded-2xl border border-border bg-muted p-2 text-left font-semibold text-[13px] outline-none transition-[transform,border-color] hover:-translate-y-0.5 hover:border-brand focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                    >
                      <StyleThumb style={style} />
                      {STYLE_TEXT[style].name()}
                      <small className="-mt-1 font-normal text-[11.5px] text-muted-foreground">
                        {STYLE_TEXT[style].hint()}
                      </small>
                    </button>
                  ))}
                </GlassCard>
                <Chips>
                  <Chip
                    tone="quiet"
                    onClick={() =>
                      makeVariants(m.aiFlowJustDoIt(), {
                        ...COVER_DEFAULTS,
                        channels: ['pulsify-cover'],
                      })
                    }
                  >
                    {m.aiFlowJustDoIt()}
                  </Chip>
                </Chips>
              </>
            )}
          </>
        )
      case 'logo':
        return (
          <>
            {m.aiFlowAskLogo()}
            {active && (
              <Chips>
                {LOGO_CHOICES.map((choice) => (
                  <Chip
                    key={choice.value}
                    onClick={() =>
                      answer(choice.label(), 'text', {
                        type: 'choose',
                        patch: { logo: choice.value },
                      })
                    }
                  >
                    {choice.label()}
                  </Chip>
                ))}
              </Chips>
            )}
          </>
        )
      case 'text':
        return (
          <>
            {m.aiFlowAskText()}
            {active && (
              <Chips>
                {TEXT_CHOICES.map((choice) => (
                  <Chip
                    key={choice.value}
                    onClick={() =>
                      answer(choice.label(), 'channels', {
                        type: 'choose',
                        patch: { text: choice.value },
                      })
                    }
                  >
                    {choice.label()}
                  </Chip>
                ))}
              </Chips>
            )}
          </>
        )
      case 'channels':
        return (
          <>
            {m.aiFlowAskChannels({ credits: formatCredits(EVENT_FLOW_PRICES.format) })}
            {active && (
              <>
                <GlassCard>
                  <ChannelPicker
                    value={machine.cover.channels}
                    onChange={(channels) => dispatch({ type: 'choose', patch: { channels } })}
                  />
                  <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2.5 text-muted-foreground text-sm">
                    <span className="inline-flex items-center gap-2">
                      {colors.length > 0 ? m.aiFlowBrandColors() : m.aiFlowBrandColorsNone()}
                      <BrandColors colors={colors} onChange={changeColors} />
                    </span>
                    <span className="inline-flex flex-wrap items-center gap-x-3">
                      {freeFormats > 0 && (
                        <span className="text-brand">
                          {freeFormats === 1
                            ? m.aiFlowFormatIncluded()
                            : m.aiFlowFormatsIncluded({ count: freeFormats })}
                        </span>
                      )}
                      <span>{m.aiFlowTotal({ credits: formatCredits(coverTotal) })}</span>
                    </span>
                  </div>
                </GlassCard>
                <Chips>
                  <Chip
                    tone="go"
                    price={coverTotal}
                    onClick={() => makeVariants(m.aiFlowMakeVariants())}
                  >
                    {m.aiFlowMakeVariants()}
                  </Chip>
                </Chips>
              </>
            )}
          </>
        )
      case 'variants':
      case 'refined':
        return (
          <>
            {kind === 'variants' ? m.aiFlowVariants() : m.aiFlowRefined()}
            {active && variants.length > 0 && (
              <>
                {kind === 'variants' && (
                  <GlassCard className="grid grid-cols-2 gap-2">
                    {variantUrls.map((url, index) => (
                      <button
                        key={url}
                        type="button"
                        aria-pressed={index === chosen}
                        aria-label={m.aiFlowVariantLabel({ number: index + 1 })}
                        onClick={() => setChosen(index)}
                        className={cn(
                          'relative aspect-[3/2] overflow-hidden rounded-xl border-2 outline-none [container-type:size] focus-visible:ring-2 focus-visible:ring-ring',
                          index === chosen ? 'border-brand' : 'border-transparent',
                        )}
                      >
                        <img src={url} alt="" className="size-full object-cover" />
                        <LogoOverlay
                          logoUrl={logoUrl}
                          placement={machine.cover.logo}
                          large={machine.cover.logoLarge}
                        />
                      </button>
                    ))}
                  </GlassCard>
                )}
                {kind === 'refined' && variantUrls[chosen] && (
                  <div className="relative mt-3 aspect-[3/2] w-full max-w-md overflow-hidden rounded-xl [container-type:size]">
                    <img src={variantUrls[chosen]} alt="" className="size-full object-cover" />
                    <LogoOverlay
                      logoUrl={logoUrl}
                      placement={machine.cover.logo}
                      large={machine.cover.logoLarge}
                    />
                  </div>
                )}
                <Chips>
                  {REFINE_CHIPS.map((label) => (
                    <Chip
                      key={label()}
                      price={EVENT_FLOW_PRICES.refine}
                      onClick={() => refine(label())}
                    >
                      {label()}
                    </Chip>
                  ))}
                  {logoUrl && machine.cover.logo !== 'none' && !machine.cover.logoLarge && (
                    <Chip
                      onClick={() =>
                        answer(m.aiFlowRefineLogoBigger(), 'refined', {
                          type: 'choose',
                          patch: { logoLarge: true },
                        })
                      }
                    >
                      {m.aiFlowRefineLogoBigger()}
                    </Chip>
                  )}
                  <Chip
                    tone="go"
                    price={finalizePrice(machine.flow) || null}
                    onClick={() => takeVariant(m.aiFlowUseThis())}
                  >
                    {m.aiFlowUseThis()}
                  </Chip>
                  {coverSource === 'photo' && (
                    <Chip
                      price={EVENT_FLOW_PRICES.refine}
                      onClick={() => refine(OTHER_LETTERING, m.aiFlowOtherLettering())}
                    >
                      {m.aiFlowOtherLettering()}
                    </Chip>
                  )}
                  <Chip
                    tone="quiet"
                    price={coverPrice(machine.flow)}
                    onClick={() =>
                      coverSource === 'photo'
                        ? makePhotoVariants(m.aiFlowMoreVariants())
                        : makeVariants(m.aiFlowMoreVariants())
                    }
                  >
                    {m.aiFlowMoreVariants()}
                  </Chip>
                </Chips>
              </>
            )}
          </>
        )
      case 'cover-done':
      case 'photo-done':
        return (
          <>
            {kind === 'cover-done' ? m.aiFlowCoverDone() : m.aiFlowPhotoDone()}
            {made.length > 0 && (
              <GlassCard className="flex flex-wrap items-end gap-2">
                {made.map((item) => (
                  <a
                    key={item.asset.id}
                    href={item.asset.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex flex-col items-center gap-1 rounded-xl border border-border bg-muted p-1.5 text-[11px] text-muted-foreground"
                  >
                    <img
                      src={item.asset.url}
                      alt={channelLabel(item.channel)}
                      className="h-24 w-auto rounded-md"
                    />
                    {channelLabel(item.channel)}
                  </a>
                ))}
              </GlassCard>
            )}
            {active && made.length > 0 && (
              <Chips>
                {coverOnly ? (
                  <Chip tone="go" onClick={() => takeCover()}>
                    {m.aiFlowToForm()}
                  </Chip>
                ) : (
                  <Chip tone="go" onClick={() => answer(m.aiFlowToDescription(), 'description')}>
                    {m.aiFlowToDescription()}
                  </Chip>
                )}
              </Chips>
            )}
          </>
        )
      case 'photo':
        return (
          <>
            {m.aiFlowAskPhoto()}
            {active && (
              <>
                {photoUrl && (manualPhoto || !photoTitle) && (
                  <GlassCard>
                    <PhotoEditor
                      photoUrl={photoUrl}
                      layout={layout}
                      onChange={setLayout}
                      brandColors={colors}
                      logoUrl={logoUrl}
                    />
                    <ChannelPicker
                      value={machine.cover.channels}
                      onChange={(channels) => dispatch({ type: 'choose', patch: { channels } })}
                      extend={extend}
                      onExtendChange={setExtend}
                      photoAspect={photoAspect}
                    />
                  </GlassCard>
                )}
                {photoUrl && !manualPhoto && photoTitle && (
                  <div className="mt-3 aspect-[3/2] w-full max-w-md overflow-hidden rounded-xl">
                    <img src={photoUrl} alt="" className="size-full object-cover" />
                  </div>
                )}
                <Chips>
                  {photoUrl && !manualPhoto && photoTitle && (
                    <Chip
                      tone="go"
                      price={coverPrice(machine.flow)}
                      onClick={() => makePhotoVariants(m.aiFlowPhotoAi())}
                    >
                      {m.aiFlowPhotoAi()}
                    </Chip>
                  )}
                  {photoUrl && (manualPhoto || !photoTitle) && (
                    <Chip
                      tone="go"
                      price={photoTotal}
                      onClick={() => makePhotoFormats(m.aiFlowPhotoMake())}
                    >
                      {m.aiFlowPhotoMake()}
                    </Chip>
                  )}
                  {photoUrl && !manualPhoto && photoTitle && (
                    <Chip tone="quiet" onClick={() => setManualPhoto(true)}>
                      {m.aiFlowPhotoManual()}
                    </Chip>
                  )}
                  <Chip onClick={() => photoInputRef.current?.click()}>
                    {photoUrl ? m.aiFlowPhotoOther() : m.aiFlowPhotoPick()}
                  </Chip>
                </Chips>
              </>
            )}
          </>
        )
      case 'description':
        return (
          <>
            {m.aiFlowAskDescription()}
            {active ? (
              <GlassCard className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`${formId}-primary`} className="text-muted-foreground">
                    {languageLabel(language)}
                  </Label>
                  <Textarea
                    id={`${formId}-primary`}
                    value={primaryText}
                    rows={6}
                    onChange={(event) => setPrimaryText(event.target.value)}
                  />
                </div>
                {secondaryText !== null && (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={`${formId}-secondary`} className="text-muted-foreground">
                      {languageLabel(otherLanguage)}
                    </Label>
                    <Textarea
                      id={`${formId}-secondary`}
                      value={secondaryText}
                      rows={6}
                      onChange={(event) => setSecondaryText(event.target.value)}
                    />
                  </div>
                )}
                <Chips>
                  {secondaryText === null && (
                    <Chip price={translationPrice(machine.flow)} onClick={() => translate()}>
                      {otherLanguage === 'en' ? m.aiFlowAddEnglish() : m.aiFlowAddGerman()}
                    </Chip>
                  )}
                  <Chip
                    tone="go"
                    onClick={() => answer(m.aiFlowDescriptionNext(), afterDetails(details))}
                  >
                    {m.aiFlowDescriptionNext()}
                  </Chip>
                </Chips>
              </GlassCard>
            ) : null}
          </>
        )
      case 'when':
        return (
          <>
            {m.aiFlowAskWhen()}
            {details && (
              <WhenQuestion
                details={details}
                active={active}
                onAnswer={(patch, label) => settle('when', patch, label)}
                onSkip={() => settle('when', {}, m.aiFlowSkip())}
              />
            )}
          </>
        )
      case 'place':
        return (
          <>
            {details ? placePrompt(details) : m.aiFlowAskPlace()}
            {details && (
              <PlaceQuestion
                businessId={businessId}
                details={details}
                active={active}
                onAnswer={(patch, label) => settle('place', patch, label)}
                onSkip={() => settle('place', {}, m.aiFlowSkip())}
              />
            )}
          </>
        )
      case 'entry':
        return (
          <>
            {details ? entryPrompt(details) : m.entryDescription()}
            {details && draft && (
              <EntryQuestion
                details={details}
                price={draft.price}
                active={active}
                onAnswer={(patch, label) => settle('entry', patch, label)}
                onSkip={() => settle('entry', {}, m.aiFlowSkip())}
              />
            )}
          </>
        )
      case 'category':
        return (
          <>
            {m.aiFlowAskCategory()}
            {details && (
              <CategoryQuestion
                details={details}
                active={active}
                onAnswer={(patch, label) => settle('category', patch, label)}
                onSkip={() => settle('category', {}, m.aiFlowSkip())}
              />
            )}
          </>
        )
      case 'features':
      case 'preview':
      case 'review':
        return (
          <>
            {m.aiFlowReview()}
            {active && details && draft && (
              <>
                <ReviewCard
                  businessId={businessId}
                  details={details}
                  price={draft.price}
                  onChange={(patch) => setDetails({ ...details, ...patch })}
                  onAsk={askAgain}
                />
                <Chips>
                  <Chip tone="go" onClick={() => finish()}>
                    {m.aiFlowToForm()}
                  </Chip>
                  <Chip onClick={() => finish(true)}>{m.saveAsDraft()}</Chip>
                </Chips>
              </>
            )}
          </>
        )
      case 'noted':
        return m.aiFlowNoted()
      case 'failed':
        return m.aiFlowFailed()
    }
  }

  const placeholder = started
    ? inRefine
      ? m.aiFlowInputRefine()
      : m.aiFlowInputNote()
    : m.aiFlowInputStart()

  const costPill = (
    <span className="rounded-full border border-border bg-muted px-3 py-1.5 font-mono text-muted-foreground text-xs tabular-nums backdrop-blur-md">
      {balance
        ? m.aiFlowCostPill({
            cost: formatCredits(machine.spent),
            balance: formatCredits(balance.credits),
          })
        : m.aiFlowCostPillNoBalance({ cost: formatCredits(machine.spent) })}
    </span>
  )

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-chat-dim backdrop-blur-xl data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <Dialog.Content
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            chatInputRef.current?.focus()
          }}
          onPaste={handlePaste}
          onDragOver={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          onDrop={handleDrop}
          className="dark ai-chat-scene fixed inset-0 z-50 flex flex-col text-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none"
        >
          <div className="ai-chat-morph" aria-hidden>
            <span />
            <span />
            <span />
          </div>
          <Dialog.Title className="sr-only">
            {m.aiFlowTitle()}
            {about.title ? ` · ${about.title}` : ''}
          </Dialog.Title>
          <Dialog.Description className="sr-only">{m.aiChatDescription()}</Dialog.Description>

          <header className="relative z-10 flex h-[72px] flex-none items-center justify-between gap-3 px-4 sm:px-6">
            <FlowAvatar className="h-8 w-[26px]" />
            <div className="absolute left-1/2 hidden -translate-x-1/2 sm:block">{costPill}</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={restart}
                disabled={!talking || thinking}
                className={cn(GLASS_BUTTON, 'px-4 disabled:opacity-40')}
              >
                <RotateCcw className="size-4" aria-hidden />
                {m.aiChatRestart()}
              </button>
              <Dialog.Close
                aria-label={m.aiChatClose()}
                title={m.aiChatClose()}
                className={cn(GLASS_BUTTON, 'w-10')}
              >
                <X className="size-[18px]" aria-hidden />
              </Dialog.Close>
            </div>
          </header>

          <div className="relative z-10 mx-auto flex min-h-0 w-full max-w-[720px] flex-1 flex-col px-4">
            {talking ? (
              <div ref={scrollRef} className="ai-chat-transcript min-h-0 flex-1 overflow-y-auto">
                <div
                  className="flex min-h-full flex-col justify-end gap-6 pt-10 pb-4"
                  aria-live="polite"
                >
                  {machine.turns.map((turn) =>
                    turn.role === 'user' ? (
                      <UserMessage key={turn.id} text={turn.text} />
                    ) : (
                      <AssistantMessage key={turn.id}>
                        {renderAssistant(turn.kind, turn.id === activeId && !thinking)}
                      </AssistantMessage>
                    ),
                  )}
                  {pending !== null && <UserMessage text={pending} />}
                  {thinking && <ThinkingDots />}
                </div>
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 overflow-y-auto py-6 text-center">
                <FlowAvatar className="h-[72px] w-14" />
                <h2 className="text-balance font-semibold text-[clamp(26px,4vw,36px)] tracking-[-0.02em]">
                  {coverOnly ? m.aiChatCoverHelloTitle() : m.aiFlowHelloTitle()}
                </h2>
                <p className="max-w-[46ch] text-muted-foreground">
                  {coverOnly ? m.aiChatCoverHelloSubtitle() : m.aiFlowHelloSubtitle()}
                </p>
                {coverOnly ? (
                  startFailed && (
                    <Chips>
                      <Chip tone="go" onClick={() => startCover()}>
                        {m.aiChatRetry()}
                      </Chip>
                    </Chips>
                  )
                ) : (
                  <>
                    <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                      {EXAMPLES.map((example) => (
                        <Chip
                          key={example()}
                          className="h-auto min-h-8 py-1.5 text-left"
                          onClick={() => setInput(example())}
                        >
                          {example()}
                        </Chip>
                      ))}
                    </div>
                    {pictures.length > 0 && (
                      <div className="mt-2 flex flex-wrap justify-center gap-2">
                        {pictures.map((file, index) => (
                          <div
                            key={previews[index]}
                            className="relative size-16 overflow-hidden rounded-xl border border-border bg-muted"
                          >
                            <img
                              src={previews[index]}
                              alt={file.name}
                              className="size-full object-cover"
                            />
                            <button
                              type="button"
                              aria-label={m.eventAiStartRemovePicture()}
                              onClick={() => setPictures(pictures.filter((_, i) => i !== index))}
                              className="absolute top-1 right-1 rounded-full bg-chat-pill p-0.5 text-chat-pill-ink"
                            >
                              <X className="size-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {screenshotHint && (
                      <p role="status" className="max-w-[560px] text-balance text-sm">
                        {screenshotHint}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex flex-none flex-col gap-2.5 pt-3 pb-5 sm:pb-8">
              {!coverOnly && !talking && pictures.length > 0 && !input.trim() && (
                <div className="flex justify-center">
                  <Chip
                    tone="go"
                    price={EVENT_PACKAGE_CREDIT_COST}
                    onClick={() => startDraft('', pictures)}
                  >
                    {m.aiFlowStartWithPictures()}
                  </Chip>
                </div>
              )}
              <ChatInput
                inputRef={chatInputRef}
                value={input}
                onChange={setInput}
                onSubmit={submitInput}
                placeholder={placeholder}
                disabled={thinking || (coverOnly && !started)}
                canSubmit={!started || inRefine || activeKind !== undefined}
                submitLabel={
                  started
                    ? m.aiFlowSend()
                    : m.aiFlowStartSubmit({ credits: formatCredits(EVENT_PACKAGE_CREDIT_COST) })
                }
                actions={
                  acceptsStartPictures && pictures.length < EVENT_DRAFT_MAX_IMAGES ? (
                    <InputAction
                      label={m.aiFlowAddPicture()}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Paperclip className="size-[18px]" />
                    </InputAction>
                  ) : null
                }
              />
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center text-muted-foreground text-xs">
                <span className="sm:hidden">{costPill}</span>
                {!coverOnly && !talking ? (
                  <span>
                    {m.aiFlowStartPrice({ credits: formatCredits(EVENT_PACKAGE_CREDIT_COST) })}{' '}
                    {m.aiFlowPicturesHint()}
                  </span>
                ) : (
                  <span>{m.aiFlowFooter()}</span>
                )}
              </div>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.heic,.heif"
            multiple
            hidden
            onChange={(event) => {
              addStartPictures(Array.from(event.target.files ?? []))
              event.target.value = ''
            }}
          />
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*,.heic,.heif"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) takePhoto(file)
              event.target.value = ''
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function LogoOverlay({
  logoUrl,
  placement,
  large,
}: {
  logoUrl: string | null
  placement: LogoPlacement
  large: boolean
}) {
  if (!logoUrl || placement === 'none') return null
  return (
    <img
      src={logoUrl}
      alt=""
      className={cn(
        'absolute object-contain',
        placement === 'top-right'
          ? cn('top-[5cqmin] right-[5cqmin]', large ? 'size-[25cqmin]' : 'size-[18cqmin]')
          : cn(
              'bottom-[5cqmin] left-1/2 -translate-x-1/2',
              large ? 'size-[17cqmin]' : 'size-[12cqmin]',
            ),
      )}
    />
  )
}
